import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import uuid from 'uuid';

import { Spinner } from 'components/atoms/Loading';
import { SubmissionCommentSection } from 'components/organisms/Task/TaskSection';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import courseService from 'services/course';
import feedbackService from 'services/feedback';
import {
    AlertDialog,
    Button,
    FileUpload,
    Icon,
    Link,
    ResourceChip,
    RichTextArea,
    Stack,
    Text,
    TextInput,
} from 'shell/components';
import { SUBMISSION_TYPES, TYPES } from 'utils/constants';
import { uploadFile } from 'utils/file';
import { downloadFileFromURL, getAbsoluteURL, getS3FileURL, getUserPreferredFullName } from 'utils/helper';

const FileSubmission = ({ mode, files, setFiles, submission, getUserByAccountId }) => {
    const { auth } = useAuth();

    const handleChange = async (files) => {
        const file = files[0];
        file.uid = uuid.v4();
        setFiles((current) =>
            current.some((currentFile) => currentFile.name === file.name)
                ? current
                : [...current, { uid: file.uid, name: file.name, isUploading: true }]
        );
        uploadFile(
            file,
            () => {},
            (data) => {
                setFiles((current) => {
                    return current.map((currentFile) =>
                        currentFile.uid === file.uid ? { ...data, size: file.size } : currentFile
                    );
                });
            },
            (e) => console.error('File upload failed.', e),
            auth.owner_account_id
        );
    };

    return mode === 'submit' ? (
        <>
            {!files.length ? (
                <>
                    <Text variant={'p2'}>File Upload</Text>
                    <FileUpload onChange={handleChange} />
                </>
            ) : (
                <Stack>
                    {files.map((file) => {
                        return (
                            <ResourceChip
                                key={file.uid || file.key}
                                options={[{ key: 'delete', value: 'Delete' }]}
                                resource={{ active: true, loading: file.isUploading, data: file }}
                                size={'small'}
                                type={'file'}
                                onSelect={({ key }) => {
                                    if (key === 'delete') {
                                        setFiles((current) => current.filter(({ key }) => key !== file.key));
                                    }
                                }}
                            />
                        );
                    })}
                </Stack>
            )}
        </>
    ) : (
        <Stack spacing={2}>
            {submission?.files?.map((file) => (
                <ResourceChip
                    key={file.id}
                    options={[{ key: 'download', value: 'Download' }]}
                    resource={file}
                    size={'small'}
                    type={'file'}
                    onSelect={({ key }) => {
                        if (key === 'download') {
                            downloadFileFromURL(getS3FileURL(file.data), file.data.name);
                        }
                    }}
                />
            ))}
            <Text color={'light'} variant={'p3'}>
                Submitted on {moment(submission.created_on).format('MMM D [@] h:mma')} by{' '}
                {getUserPreferredFullName(getUserByAccountId(submission.owner_account_id))}
            </Text>
        </Stack>
    );
};

const TextSubmission = ({ mode, text, setText, submission, viewSubmission, getUserByAccountId }) => {
    return mode === 'submit' ? (
        <RichTextArea
            label={'Text Entry'}
            labelId={'submission-input'}
            placeholder={'Enter submission here'}
            value={text}
            onChange={setText}
        />
    ) : (
        <Stack spacing={2}>
            <Button size={'small'} variant={'secondary'} onClick={() => viewSubmission(submission)}>
                View submission details
            </Button>
            <Text color={'light'} variant={'p3'}>
                Submitted on {moment(submission.created_on).format('MMM D [@] h:mma')} by{' '}
                {getUserPreferredFullName(getUserByAccountId(submission.owner_account_id))}
            </Text>
        </Stack>
    );
};

const LinkSubmission = ({ mode, url, setUrl, submission, getUserByAccountId }) => {
    return mode === 'submit' ? (
        <>
            <Text variant={'p2'}>Website URL</Text>
            <TextInput
                startIcon={<Icon kind={'link'} />}
                placeholder={'Enter URL submission'}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
            />
        </>
    ) : (
        <Stack spacing={2}>
            <Link
                external
                href={getAbsoluteURL(submission?.url?.url || '')}
                rel={'noopener noreferrer'}
                startIcon={<Icon kind={'link'} />}
                target={'_blank'}
            >
                {submission?.url?.url}
            </Link>
            <Text color={'light'} variant={'p3'}>
                Submitted on {moment(submission.created_on).format('MMM D [@] h:mma')} by{' '}
                {getUserPreferredFullName(getUserByAccountId(submission.owner_account_id))}
            </Text>
        </Stack>
    );
};

const SubmissionSection = ({ isOpen, task, getUserByAccountId, setSubmissionsState, updateTask }) => {
    const { auth } = useAuth();
    const { message } = useSnackbar();

    const commentInputRef = useRef(null);

    const [confirmationModal, setConfirmationModal] = useState({
        visible: false,
        destructive: false,
        title: '',
        content: '',
        confirmText: 'Submit',
        onConfirm: () => {},
        onCancel: () => {},
    });
    const [submissions, setSubmissions] = useState(null);
    const [mode, setMode] = useState('submit');

    // submission_type is file
    const [files, setFiles] = useState([]);
    // submission_type is plaintext
    const [text, setText] = useState('');
    // submission_type is url
    const [url, setUrl] = useState('');
    // all submission types can have a comment
    const [comment, setComment] = useState('');
    const [currentEditingComment, setCurrentEditingComment] = useState(null);

    const submissionFooterEl = document.getElementById('milestone-submission-footer');

    const closeConfirmationModal = () => {
        setConfirmationModal((current) => ({
            ...current,
            visible: false,
            onConfirm: () => {},
            onCancel: () => {},
        }));
    };

    const handleClickSubmit = () => {
        if (mode === 'submit') {
            if (task.submission_type === SUBMISSION_TYPES.FILE && !files.length) {
                return message.error('Please attach a file before submitting.');
            }
            if (task.submission_type === SUBMISSION_TYPES.PLAINTEXT && !text) {
                return message.error('Please fill in the text entry box before submitting.');
            }
            if (task.submission_type === SUBMISSION_TYPES.FILE && !files.length) {
                return message.error('Please fill in the URL box before submitting.');
            }
            setConfirmationModal((current) => ({
                ...current,
                visible: true,
                destructive: false,
                content: 'This submission will count for all group members.',
                onConfirm: () => {
                    const now = new Date().getTime();
                    const submissionId = uuid.v4();
                    const submission = {
                        id: submissionId,
                        active: true,
                        created_on: now,
                        modified_on: now,
                        owner_account_id: auth.owner_account_id,
                        course_id: task.course_id,
                        group_id: task.group_id,
                        type: TYPES.MILESTONE,
                        milestone_id: task.milestone_id,
                        submission_type: task.submission_type,
                        comments: comment
                            ? [
                                  {
                                      id: uuid.v4(),
                                      active: true,
                                      created_on: now,
                                      modified_on: now,
                                      owner_account_id: auth.owner_account_id,
                                      course_id: task.course_id,
                                      group_id: task.group_id,
                                      text: comment,
                                      type: TYPES.SUBMISSION,
                                      submission_id: submissionId,
                                  },
                              ]
                            : [],
                    };
                    if (task.submission_type === SUBMISSION_TYPES.FILE) {
                        submission.files = files.map((file) => ({
                            id: uuid.v4(),
                            active: true,
                            created_on: now,
                            modified_on: now,
                            owner_account_id: auth.owner_account_id,
                            course_id: task.course_id,
                            group_id: task.group_id,
                            data: file,
                            public: true,
                            type: TYPES.SUBMISSION,
                            submission_id: submissionId,
                            submission_type: TYPES.MILESTONE,
                            milestone_id: task.milestone_id,
                        }));
                    }
                    if (task.submission_type === SUBMISSION_TYPES.PLAINTEXT) {
                        submission.plaintext = text;
                    }
                    if (task.submission_type === SUBMISSION_TYPES.URL) {
                        submission.url = {
                            id: uuid.v4(),
                            active: true,
                            created_on: now,
                            modified_on: now,
                            owner_account_id: auth.owner_account_id,
                            course_id: task.course_id,
                            group_id: task.group_id,
                            url: url,
                            name: url,
                            public: true,
                            type: TYPES.SUBMISSION,
                            submission_id: submissionId,
                            submission_type: TYPES.MILESTONE,
                            milestone_id: task.milestone_id,
                        };
                    }
                    setSubmissions((current) => [submission, ...current]);
                    setMode('view');
                    setFiles([]);
                    setText('');
                    setUrl('');
                    setComment('');
                    setCurrentEditingComment(null);
                    closeConfirmationModal();
                    message.success('Your milestone has been submitted.');
                    courseService.createCourseSubmission(auth.course_id, submission);
                    updateTask(task.id, 'status', 'completed', true);
                },
                onCancel: closeConfirmationModal,
            }));
        } else {
            setMode('submit');
        }
    };

    const handleAddComment = (html) => {
        const now = new Date().getTime();
        const newComment = {
            id: uuid.v4(),
            active: true,
            created_on: now,
            modified_on: now,
            owner_account_id: auth.owner_account_id,
            course_id: task.course_id,
            group_id: task.group_id,
            text: html.trim(),
            type: TYPES.SUBMISSION,
            submission_id: submissions[0].id,
        };
        setSubmissions((current) => {
            return current.map((submission, index) => {
                if (index !== 0) return submission;
                return {
                    ...submission,
                    comments: [...submission.comments, newComment],
                };
            });
        });
        feedbackService.createFeedback(newComment);
    };

    const handleEditComment = (html) => {
        const updatedComment = {
            ...currentEditingComment,
            modified_on: new Date().getTime(),
            text: html.trim(),
        };
        setSubmissions((current) => {
            return current.map((submission, index) => {
                if (index !== 0) return submission;
                return {
                    ...submission,
                    comments: submission.comments.map((comment) => {
                        return comment.id === currentEditingComment.id ? updatedComment : comment;
                    }),
                };
            });
        });
        feedbackService.updateFeedback(updatedComment.id, updatedComment);
        setCurrentEditingComment(null);
    };

    const handleSelect = (value, comment) => {
        if (value === 'edit') {
            setCurrentEditingComment(comment);
            setTimeout(() => {
                commentInputRef.current.props.editor.commands.focus();
            }, 100);
        }
        if (value === 'delete') {
            setConfirmationModal((current) => ({
                ...current,
                visible: true,
                destructive: true,
                title: 'Delete Comment?',
                content: 'Are you sure you want to permanently delete this comment?',
                confirmText: 'Delete',
                onConfirm: () => {
                    setSubmissions((current) => {
                        return current.map((submission, index) => {
                            if (index !== 0) return submission;
                            return {
                                ...submission,
                                comments: submission.comments.filter(({ id }) => id !== comment.id),
                            };
                        });
                    });
                    closeConfirmationModal();
                    feedbackService.deleteFeedback(comment.id);
                },
                onCancel: closeConfirmationModal,
            }));
        }
    };

    useEffect(() => {
        const fetchSubmissions = async () => {
            setSubmissions(null);
            setMode('submit');
            const submissions = await courseService.listCourseSubmissions(
                task.course_id,
                JSON.stringify({
                    active: true,
                    group_id: task.group_id,
                    type: TYPES.MILESTONE,
                    milestone_id: task.milestone_id,
                })
            );
            setSubmissions(submissions.sort((a, b) => b.created_on - a.created_on));
            if (submissions.length) setMode('view');
        };
        if (isOpen) {
            fetchSubmissions();
        } else {
            setSubmissions(null);
            setMode('submit');
            setFiles([]);
            setText('');
            setUrl('');
            setComment('');
            setCurrentEditingComment(null);
        }
    }, [isOpen, task.id]);

    return (
        <>
            {!submissions ? (
                <Spinner style={{ marginTop: '5vh' }} />
            ) : (
                <Stack spacing={2}>
                    {mode === 'submit' ? (
                        <Text variant={'p2'} weight={'semibold'}>
                            Submission
                        </Text>
                    ) : (
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Icon
                                sx={{
                                    fontSize: '0.875rem',
                                    color: (theme) => theme.palette.ensightful.success[600],
                                }}
                                kind={'checkmark'}
                            />
                            <Text variant={'p2'} weight={'semibold'}>
                                Submitted!
                            </Text>
                        </Stack>
                    )}
                    {task.submission_type === SUBMISSION_TYPES.FILE && (
                        <FileSubmission
                            mode={mode}
                            files={files}
                            setFiles={setFiles}
                            submission={submissions[0]}
                            getUserByAccountId={getUserByAccountId}
                        />
                    )}
                    {task.submission_type === SUBMISSION_TYPES.PLAINTEXT && (
                        <TextSubmission
                            mode={mode}
                            text={text}
                            setText={setText}
                            submission={submissions[0]}
                            viewSubmission={() => setSubmissionsState({ isOpen: true, submissions })}
                            getUserByAccountId={getUserByAccountId}
                        />
                    )}
                    {task.submission_type === SUBMISSION_TYPES.URL && (
                        <LinkSubmission
                            mode={mode}
                            url={url}
                            setUrl={setUrl}
                            submission={submissions[0]}
                            getUserByAccountId={getUserByAccountId}
                        />
                    )}
                    {mode === 'submit' ? (
                        <RichTextArea
                            label={'Comments'}
                            labelId={'submission-comments'}
                            placeholder={'Add comments (optional)'}
                            value={comment}
                            onChange={setComment}
                        />
                    ) : (
                        <SubmissionCommentSection
                            ref={commentInputRef}
                            submissions={submissions}
                            getUserByAccountId={getUserByAccountId}
                            handleSelect={handleSelect}
                            currentEditingComment={currentEditingComment}
                            setCurrentEditingComment={setCurrentEditingComment}
                            handleAddComment={handleAddComment}
                            handleEditComment={handleEditComment}
                        />
                    )}
                </Stack>
            )}
            {submissionFooterEl &&
                createPortal(
                    <>
                        <Button
                            data-cy={'submit-assignment-button'}
                            disabled={!submissions || files.some(({ isUploading }) => isUploading)}
                            onClick={handleClickSubmit}
                        >
                            {mode === 'submit' ? 'Submit Assignment' : 'Resubmit Assignment'}
                        </Button>
                        {mode === 'view' && (
                            <Button
                                color={'neutral'}
                                variant={'tertiary'}
                                onClick={() => setSubmissionsState({ isOpen: true, mode: 'history', submissions })}
                            >
                                View History
                            </Button>
                        )}
                    </>,
                    submissionFooterEl
                )}
            <AlertDialog
                open={confirmationModal.visible}
                variant={confirmationModal.destructive ? 'destructive' : 'primary'}
                message={confirmationModal.title}
                description={confirmationModal.content}
                actionButtonText={confirmationModal.confirmText}
                onAction={confirmationModal.onConfirm}
                onCancel={confirmationModal.onCancel}
                onClose={confirmationModal.onCancel}
            />
        </>
    );
};

export default SubmissionSection;
